import { ReactElement, ReactNode } from 'react';
import classnames from 'classnames';
import { AlertTriangle, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DiscountCardTag as Tag } from './useDiscountCardTag';

interface Props {
  tag: Tag;
  /**
   * Classic deals cards render on a coloured gradient (white text); image cards render on a
   * white surface (toned text). This flips the palette so the status row stays legible on both.
   */
  onColoredBg?: boolean;
}

/**
 * FOO-2555 / PROD-407 Inc 3 — the availability / applied / replaces status row on a discount card.
 *
 * Rendered as an inline icon + text row (matching the PM / Lovable prototype `StatusRow`), not a
 * pill: "✓ Available now", "✓ Applied · €4.00 off", "⚠ Cannot be combined with CHEVERE". Purely
 * presentational; the {@link Tag} descriptor is computed by `useDiscountCardTag`. The blocked
 * state's follow-up note + "Replaces …" line are rendered by the card around this row.
 */
export default function DiscountCardTag({ tag, onColoredBg }: Props): ReactElement {
  const { t } = useTranslation(null, { keyPrefix: 'Features:DiscountExclusivity.card' });

  if (tag.status === 'applied') {
    return (
      <Row status="applied" tone="success" onColoredBg={onColoredBg} icon={<Check className="w-3.5 h-3.5 shrink-0" />}>
        {tag.savings ? t('applied', { savings: tag.savings }) : t('applied_plain')}
      </Row>
    );
  }

  if (tag.status === 'replaces') {
    const replacesLabel =
      tag.activeKind === 'offer' && (tag.activeCount ?? 0) > 1
        ? t('cannot_combine_offers', { count: tag.activeCount })
        : tag.activeName
          ? t('cannot_combine', { name: tag.activeName })
          : t('replaces_no_name', { label: t(`kind.${tag.activeKind}`) });

    return (
      <Row
        status="replaces"
        tone="warning"
        onColoredBg={onColoredBg}
        icon={<AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
      >
        {replacesLabel}
      </Row>
    );
  }

  // No icon on "Available now" — the checkmark is reserved for applied / auto-applied discounts.
  return (
    <Row status="available" tone="success" onColoredBg={onColoredBg} icon={null}>
      {t('available')}
    </Row>
  );
}

interface RowProps {
  status: 'available' | 'applied' | 'replaces';
  tone: 'success' | 'warning';
  onColoredBg?: boolean;
  icon: ReactNode;
  children: ReactNode;
}

const Row = ({ status, tone, onColoredBg, icon, children }: RowProps): ReactElement => {
  const toneClass = onColoredBg ? 'text-white' : tone === 'warning' ? 'text-amber-600' : 'text-success';

  return (
    <p
      data-test="discount-card-tag"
      data-status={status}
      className={classnames('flex items-center gap-1.5 text-xs font-medium leading-tight', toneClass)}
    >
      {icon}
      <span>{children}</span>
    </p>
  );
};
